import React, { useEffect, useRef, useState, useCallback } from 'react';

interface RadioMessage {
  id?: number;
  timestamp: string;
  indicatif_emetteur: string;
  destinataire: string;
  message: string;
  statut_radio: number | null;
  phase: string | null;
  is_cos_message: 0 | 1;
}

interface Props {
  instanceId: number;
  /** Incrémenter pour forcer un rechargement (après chaque avancement) */
  refreshKey?: number;
}

const STATUT_COLORS: Record<number, string> = {
  2: 'text-amber-300',
  3: 'text-emerald-300',
  4: 'text-cyan-300',
  7: 'text-emerald-400',
  0: 'text-red-400',
};

function formatTime(ts: string): string {
  try {
    return new Date(ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return ts;
  }
}

const RadioJournal: React.FC<Props> = ({ instanceId, refreshKey }) => {
  const [messages, setMessages] = useState<RadioMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef<HTMLDivElement>(null);

  const fetchJournal = useCallback(() => {
    setLoading(true);
    const token = localStorage.getItem('token');
    fetch(`/api/cta/interventions-instance/${instanceId}/radio-journal`, {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    })
      .then((r) => r.json())
      .then((data) => {
        setMessages(data.journal ?? []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [instanceId]);

  useEffect(() => { fetchJournal(); }, [fetchJournal, refreshKey]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (loading && !messages.length) {
    return <p className="px-2 py-1 text-[10px] text-slate-500">Chargement journal radio…</p>;
  }

  if (!messages.length) {
    return <p className="px-2 py-1 text-[10px] text-slate-500 italic">Le journal s'alimentera lors de l'avancement de l'intervention.</p>;
  }

  return (
    <div className="flex flex-col gap-0.5 max-h-52 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-slate-700">
      {messages.map((m, i) => (
        <div
          key={m.id ?? i}
          className={`flex gap-2 rounded px-2 py-1 text-[10px] leading-snug font-mono ${
            m.is_cos_message ? 'bg-amber-950/25 border border-amber-700/25' : 'bg-slate-900/30'
          }`}
        >
          <span className="shrink-0 text-slate-600 tabular-nums">{formatTime(m.timestamp)}</span>
          <span className={`shrink-0 font-bold min-w-0 truncate max-w-[8rem] ${
            m.statut_radio != null ? (STATUT_COLORS[m.statut_radio] ?? 'text-slate-300') : 'text-amber-200'
          }`}>
            {m.indicatif_emetteur}
          </span>
          <span className="text-slate-300 break-words min-w-0">{m.message}</span>
        </div>
      ))}
      <div ref={bottomRef} />
    </div>
  );
};

export default RadioJournal;
